import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { Horse } from '../../utils/api';
import { patientLabel } from '../../utils/patientLabel';

interface AddHorsesToBarnDialogProps {
  open: boolean;
  onClose: () => void;
  barnId: string;
  barnName: string;
}

/**
 * Assign (or move) horses into this barn. Options show each horse's current
 * barn so moves are explicit ("moves from X"). Removal lives on the roster
 * row (sets barnId null via the horses API), not here.
 */
const AddHorsesToBarnDialog: FC<AddHorsesToBarnDialogProps> = ({
  open,
  onClose,
  barnId,
  barnName,
}) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Horse[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: options = [], isLoading } = useQuery(
    ['horses', 'barn-assign-picker', search],
    async () => {
      const response = await api.get('/horses', {
        params: { page: 1, limit: 50, ...(search ? { search } : {}) },
      });
      return (response.data.data ?? response.data) as Horse[];
    },
    { enabled: open, keepPreviousData: true },
  );

  const assignMutation = useMutation(
    () =>
      api
        .post(`/barns/${barnId}/horses`, { horseIds: selected.map((h) => h.id) })
        .then((r) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['barn', barnId]);
        queryClient.invalidateQueries(['barns']);
        queryClient.invalidateQueries(['horses']);
        queryClient.invalidateQueries(['horse-locations']);
        setSelected([]);
        onClose();
      },
      onError: (err: any) => setError(err?.response?.data?.message ?? 'Failed to assign horses'),
    },
  );

  const optionLabel = (horse: Horse) =>
    horse.location && horse.location !== barnName
      ? `${patientLabel(horse)} — moves from ${horse.location}`
      : patientLabel(horse);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add horses to {barnName}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            multiple
            options={options.filter((h) => h.barnId !== barnId)}
            value={selected}
            loading={isLoading}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_e, value) => setSelected(value)}
            onInputChange={(_e, value, reason) => {
              if (reason === 'input') setSearch(value);
            }}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Horses"
                placeholder="Search by name, patient ID, owner, microchip…"
                autoFocus
              />
            )}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Assigning updates each horse's Barn (and its location shown on the patient chart).
            Horses already at another barn are moved.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => assignMutation.mutate()}
          disabled={selected.length === 0 || assignMutation.isLoading}
        >
          Add {selected.length > 0 ? `${selected.length} horse${selected.length > 1 ? 's' : ''}` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddHorsesToBarnDialog;
